import { useLayoutEffect, useRef, type ReactNode } from "react";
import { Box } from "@mantine/core";
import {
  ECOSYSTEM_EXPLORE_CARD_GRID_GAP,
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import classes from "./CorridorFixedCardGrid.module.css";

type CorridorFixedCardGridProps = {
  children: ReactNode;
  /** Horizontal gap between equal-width cards. */
  gap?: number;
  verticalGap?: number;
  /** Minimum card track width (default 304). */
  cardWidth?: number;
};

/** Fluid equal columns (up to 5), space-evenly, min card width preserved, sibling height sync. */
export default function CorridorFixedCardGrid({
  children,
  gap = ECOSYSTEM_EXPLORE_CARD_GRID_GAP,
  verticalGap,
  cardWidth = ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH,
}: CorridorFixedCardGridProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    let raf = 0;

    const syncHeights = () => {
      const items = Array.from(root.children) as HTMLElement[];
      if (items.length === 0) return;

      for (const item of items) {
        item.style.height = "auto";
        item.style.minHeight = `${ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT}px`;
      }

      let tallest = ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT;
      for (const item of items) {
        tallest = Math.max(tallest, item.getBoundingClientRect().height);
      }

      const heightPx = `${Math.ceil(tallest)}px`;
      for (const item of items) {
        item.style.minHeight = heightPx;
        item.style.height = heightPx;
      }
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncHeights);
    };

    syncHeights();
    const observer = new ResizeObserver(schedule);
    observer.observe(root);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [children, cardWidth, gap, verticalGap]);

  return (
    <Box
      ref={ref}
      className={classes.grid}
      style={{
        ["--corridor-card-min-width" as string]: `${cardWidth}px`,
        ["--corridor-card-gap" as string]: `${gap}px`,
        ["--corridor-card-row-gap" as string]: `${verticalGap ?? gap}px`,
        ["--corridor-card-min-height" as string]: `${ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT}px`,
      }}
    >
      {children}
    </Box>
  );
}
